import { useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Spinner } from "../../../components/ui/spinner";
import { useTranslation } from "react-i18next";
import AccessLevelField from "./AccessLevelField";
import AdvancedSettings from "./AdvancedSettings";
import FormField from "./FormField";
import { DEFAULT_CONNECTOR_FORM, TRANSPORT_PROTOCOLS } from "../constants";
import { useConnectorForm } from "../hooks/useConnectorForm";
import { useOrganizations } from "../hooks/useOrganizations";
import { useUsers } from "../hooks/useUsers";
import type { ConnectorFormValues, TransportProtocol } from "../types";

export type ConnectorDialogMode = "create" | "edit";

type Props = {
  open: boolean;
  mode?: ConnectorDialogMode;
  initialValues?: ConnectorFormValues;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: ConnectorFormValues) => void;
  isSubmitting?: boolean;
};

const AddConnectorDialog = ({
  open,
  mode = "create",
  initialValues,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();
  const { values, setField, setAccessLevel, setAuthHeader, reset, isValid } =
    useConnectorForm();

  // Refresh the org list on every open (create and edit alike) so the sharing
  // fields resolve against the user's current organizations, not a cached id,
  // and pull the member list with it so recently added teammates are already
  // there when the access level is switched to Users.
  useOrganizations({ enabled: open });
  useUsers({ enabled: open });

  // Hold the latest initialValues without making them an effect dependency.
  // The detail GET resolves after the dialog opens and flips the reference;
  // re-seeding on every change would (a) overwrite the user's edits and
  // (b) form a render → setState → render loop when the dto stays in sync
  // with cache writes from sibling queries.
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  // Re-seed only when the dialog transitions to open — opening Edit for a
  // different connection refreshes the fields, reopening Add starts blank.
  useEffect(() => {
    if (open) {
      reset(initialValuesRef.current ?? DEFAULT_CONNECTOR_FORM);
    }
  }, [open, reset]);

  const copyKey = `mcpPage.connectorDialog.${mode}`;

  const handleCancel = useCallback(() => {
    if (isSubmitting) return;
    onOpenChange(false);
  }, [isSubmitting, onOpenChange]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!isValid || isSubmitting) return;
      // The page owns when to close — it waits for the mutation to settle so
      // errors surface before the dialog disappears.
      onSubmit(values);
    },
    [isValid, isSubmitting, onSubmit, values],
  );

  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if (isSubmitting && !next) return;
      onOpenChange(next);
    },
    [isSubmitting, onOpenChange],
  );

  const isSubmitDisabled = !isValid || isSubmitting;

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[640px]">
        <DialogHeader className="shrink-0 px-6 pt-6">
          <DialogTitle className="text-xl font-semibold">
            {t(`${copyKey}.title`)}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
          <div className="flex min-h-0 flex-col gap-5 overflow-y-auto px-6 py-5">
            <FormField
              label={t("mcpPage.fields.connectorAlias")}
              htmlFor="connector-alias"
            >
              <input
                id="connector-alias"
                type="text"
                dir={direction}
                autoFocus
                placeholder={t("mcpPage.placeholders.connectorAlias")}
                value={values.alias}
                onChange={(e) => setField("alias", e.target.value)}
                className="h-10 w-full rounded-[8px] border border-border bg-card px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
              />
            </FormField>

            <FormField
              label={t("mcpPage.fields.serverUrl")}
              htmlFor="connector-url"
            >
              <input
                id="connector-url"
                type="url"
                dir={direction}
                placeholder={t("mcpPage.placeholders.serverUrl")}
                value={values.serverUrl}
                onChange={(e) => setField("serverUrl", e.target.value)}
                className="h-10 w-full rounded-[8px] border border-border bg-card px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
              />
            </FormField>

            <FormField label={t("mcpPage.fields.transportProtocol")}>
              <Select
                dir={direction}
                value={values.transport || undefined}
                onValueChange={(value) =>
                  setField("transport", value as TransportProtocol)
                }
              >
                <SelectTrigger className="h-10 w-full rounded-[8px] border-border bg-card text-foreground">
                  <SelectValue placeholder={t("mcpPage.actions.select")} />
                </SelectTrigger>
                <SelectContent>
                  {TRANSPORT_PROTOCOLS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <AccessLevelField
              accessLevel={values.accessLevel}
              onAccessLevelChange={setAccessLevel}
              projectId={values.projectId}
              onProjectIdChange={(id) => setField("projectId", id)}
              users={values.users}
              onUsersChange={(users) => setField("users", users)}
              requireCurrentUser
            />

            <AdvancedSettings
              authHeader={values.authHeader}
              onAuthHeaderChange={setAuthHeader}
              oauthClientId={values.oauthClientId}
              onOauthClientIdChange={(value) => setField("oauthClientId", value)}
              oauthClientSecret={values.oauthClientSecret}
              onOauthClientSecretChange={(value) =>
                setField("oauthClientSecret", value)
              }
              apiKey={values.apiKey}
              onApiKeyChange={(value) => setField("apiKey", value)}
              bearerToken={values.bearerToken}
              onBearerTokenChange={(value) => setField("bearerToken", value)}
            />

            <p className="border-t border-border pt-4 text-sm text-muted-foreground">
              {t("mcpPage.connectorDialog.trustWarning")}
            </p>

          </div>

          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="h-10 rounded-[8px] border border-border bg-card px-5 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t("mcpPage.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              aria-busy={isSubmitting}
              className="flex h-10 items-center gap-2 rounded-[8px] bg-primary px-5 text-sm font-semibold text-primary-foreground transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting && <Spinner className="size-4" />}
              {isSubmitting
                ? t(`${copyKey}.submitBusy`)
                : t(`${copyKey}.submitIdle`)}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddConnectorDialog;
